import { nanoid } from 'nanoid';
import { formatDistanceToNow } from 'date-fns';
import type { 
  Artifact, ClarityExport, DigState, Layer, Presence, Revision, SelfModel 
} from './types';
import { LAYERS } from './types';

const STORAGE_KEY = 'dig.state.v1';
const KEY_KEY = 'dig.key.v1';

export function createNewDigKey(): string {
  return `dig_${nanoid(28)}`;
}

export function getStoredKey(): string | null {
  return localStorage.getItem(KEY_KEY);
}

export function storeKey(key: string) {
  localStorage.setItem(KEY_KEY, key);
}

export function clearKey() {
  localStorage.removeItem(KEY_KEY);
}

const defaultPresence: Presence = {
  workingMemoryLoad: 3,
  perceivedControl: 2,
  contextSwitches: 0,
};

const defaultModel: SelfModel = {
  statements: [
    "I feel the weight of scale and my smallness in it.",
    "There are things I keep returning to that I have not named clearly.",
  ],
  openQuestions: [
    "What would it mean to stay with this without rushing to a fix?",
  ],
  updatedAt: new Date().toISOString(),
};

function createInitialState(key: string): DigState {
  const now = new Date().toISOString();
  return {
    id: key,
    createdAt: now,
    lastActiveAt: now,
    artifacts: [],
    model: { ...defaultModel },
    revisions: [],
    currentPresence: { ...defaultPresence },
  };
}

export function loadDig(): DigState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DigState;
    // Basic migration / hygiene
    if (!parsed.currentPresence) parsed.currentPresence = { ...defaultPresence };
    if (!parsed.model) parsed.model = { ...defaultModel };
    if (!parsed.revisions) parsed.revisions = [];
    return parsed;
  } catch {
    return null;
  }
}

export function saveDig(state: DigState) {
  const toSave = {
    ...state,
    lastActiveAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
}

export function createDig(): DigState {
  const key = createNewDigKey();
  const state = createInitialState(key);
  storeKey(key);
  saveDig(state);
  return state;
}

export function resumeOrCreate(): { state: DigState; isNew: boolean; resumptionNote?: string } {
  const existingKey = getStoredKey();
  if (existingKey) {
    const loaded = loadDig();
    if (loaded && loaded.id === existingKey) {
      // Compute gentle resumption note
      const last = new Date(loaded.lastActiveAt);
      const hours = Math.round((Date.now() - last.getTime()) / (1000 * 60 * 60));
      const note = hours > 3 
        ? `You last dug ${formatDistanceToNow(last, { addSuffix: true })}.`
        : undefined;
      
      // Light context switch + resumption bump
      const updated: DigState = {
        ...loaded,
        currentPresence: {
          ...loaded.currentPresence,
          resumedAfterHours: hours > 1 ? hours : undefined,
          contextSwitches: (loaded.currentPresence.contextSwitches || 0) + (hours > 8 ? 1 : 0),
        },
      };
      saveDig(updated);
      return { state: updated, isNew: false, resumptionNote: note };
    }
  }
  // Fresh
  const fresh = createDig();
  return { state: fresh, isNew: true };
}

export function resetDig() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(KEY_KEY);
}

export function addArtifact(
  state: DigState, 
  layer: Layer, 
  text: string
): { state: DigState; artifact: Artifact } {
  const artifact: Artifact = {
    id: nanoid(10),
    layer,
    text: text.trim(),
    createdAt: new Date().toISOString(),
  };
  const next: DigState = {
    ...state,
    artifacts: [...state.artifacts, artifact],
  };
  saveDig(next);
  return { state: next, artifact };
}

export function moveArtifact(state: DigState, artifactId: string, toLayer: Layer): DigState {
  const nextArtifacts = state.artifacts.map(a =>
    a.id === artifactId ? { ...a, layer: toLayer } : a
  );
  const next: DigState = { ...state, artifacts: nextArtifacts };
  saveDig(next);
  return next;
}

export function updateArtifactText(state: DigState, artifactId: string, text: string): DigState {
  const next: DigState = {
    ...state,
    artifacts: state.artifacts.map(a =>
      a.id === artifactId ? { ...a, text: text.trim() } : a
    ),
  };
  saveDig(next);
  return next;
}

export function deleteArtifact(state: DigState, artifactId: string): DigState {
  const next: DigState = {
    ...state,
    artifacts: state.artifacts.filter(a => a.id !== artifactId),
  };
  saveDig(next);
  return next;
}

export function brushArtifact(state: DigState, artifactId: string, seconds: number): DigState {
  const next: DigState = {
    ...state,
    artifacts: state.artifacts.map(a =>
      a.id === artifactId ? { ...a, brushedSeconds: (a.brushedSeconds || 0) + seconds } : a
    ),
  };
  saveDig(next);
  return next;
}

export function updatePresence(state: DigState, patch: Partial<Presence>): DigState {
  const next: DigState = {
    ...state,
    currentPresence: { ...state.currentPresence, ...patch },
  };
  saveDig(next);
  return next;
}

export function updateModel(state: DigState, patch: Partial<SelfModel>): DigState {
  const nextModel: SelfModel = {
    ...state.model,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  const next: DigState = { ...state, model: nextModel };
  saveDig(next);
  return next;
}

export function addStatement(state: DigState, text: string): DigState {
  if (!text.trim()) return state;
  const next: DigState = {
    ...state,
    model: {
      ...state.model,
      statements: [...state.model.statements, text.trim()],
      updatedAt: new Date().toISOString(),
    },
  };
  saveDig(next);
  return next;
}

export function removeStatement(state: DigState, index: number): DigState {
  const nextStatements = state.model.statements.filter((_, i) => i !== index);
  return updateModel(state, { statements: nextStatements });
}

export function addOpenQuestion(state: DigState, text: string): DigState {
  if (!text.trim()) return state;
  return updateModel(state, {
    openQuestions: [...state.model.openQuestions, text.trim()],
  });
}

export function removeOpenQuestion(state: DigState, index: number): DigState {
  const next = state.model.openQuestions.filter((_, i) => i !== index);
  return updateModel(state, { openQuestions: next });
}

/**
 * The heart of the "self-model revision log".
 * Sealing a revision is a deliberate act. It captures the current model + presence + selected live artifacts.
 */
export function sealRevision(
  state: DigState,
  narrative: string,
  title?: string
): { state: DigState; revision: Revision } {
  const number = (state.revisions.length || 0) + 1;
  const now = new Date().toISOString();

  // Snapshot the artifacts that exist right now as the "considered" set for this revision
  const artifactIds = state.artifacts.map(a => a.id);

  const revision: Revision = {
    id: nanoid(10),
    number,
    createdAt: now,
    model: JSON.parse(JSON.stringify(state.model)), // deep snapshot
    narrative: narrative.trim() || "A small shift in how I am holding this.",
    presence: { ...state.currentPresence },
    artifactIds,
    title,
  };

  const next: DigState = {
    ...state,
    revisions: [...state.revisions, revision],
    // After sealing, we gently keep the presence but let the person update it for next time
    currentPresence: {
      ...state.currentPresence,
      // slight natural decay of "just resumed"
      resumedAfterHours: undefined,
    },
  };
  saveDig(next);
  return { state: next, revision };
}

/**
 * Produce a beautiful, self-contained export object.
 * This is the "shareable clarity" unit.
 */
export function prepareClarityExport(
  state: DigState,
  selectedRevisionIds: string[],
  selectedArtifactIds: string[],
  shareNote?: string,
  contextForReader?: string
): ClarityExport {
  const selectedRevisions = state.revisions.filter(r => 
    selectedRevisionIds.includes(r.id)
  );
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    digTitle: state.title,
    selectedRevisions: JSON.parse(JSON.stringify(selectedRevisions)),
    selectedArtifactIds,
    currentModel: JSON.parse(JSON.stringify(state.model)),
    shareNote,
    contextForReader,
  };
}

export function deleteEntireDig() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(KEY_KEY);
}

/**
 * Very light local heuristic curiosity engine.
 * Real version in Worker can call Workers AI + Vectorize for semantic memory.
 */
export function generateCuriosityPrompts(
  artifacts: Artifact[],
  model: SelfModel,
  recentLayer?: Layer
): string[] {
  const prompts: string[] = [];
  const allText = artifacts.map(a => a.text.toLowerCase()).join(' ');
  const recentCount = artifacts.length;

  // Layer-aware
  if (recentLayer === 'surface' || !recentLayer) {
    prompts.push("What specific image, sentence, or bodily sensation was present right before this feeling arrived?");
  }
  if (recentLayer === 'weather' || !recentLayer) {
    prompts.push("If this weather had a temperature and a texture, what would they be?");
  }
  if (recentLayer === 'patterns') {
    prompts.push("When did you first notice this pattern in yourself? What was happening in the world then?");
  }
  if (recentLayer === 'values' || recentLayer === 'questions') {
    prompts.push("What value or loss feels most unnamed when you say this out loud?");
  }
  if (recentLayer === 'edges') {
    prompts.push("What would 'a little more agency' look like here — not full control, just a slightly larger circle?");
  }

  // Cross-artifact memory
  if (recentCount > 3 && allText.includes('powerless') || allText.includes('helpless')) {
    prompts.push("You have used words like powerless or helpless a few times. What is the smallest unit of power you still feel in your body when you think about this?");
  }
  if (allText.includes('news') || allText.includes('scroll')) {
    prompts.push("When you encounter climate information, what part of you is trying to stay informed, and what part is trying to stay intact?");
  }

  // Model-aware
  if (model.statements.some(s => s.toLowerCase().includes('small'))) {
    prompts.push("You mentioned smallness. What scale feels honest for you to think at right now — a single relationship, a bioregion, a policy, a century?");
  }

  // Always useful defaults if we are thin
  if (prompts.length < 2) {
    prompts.push("What would change in how you carry this if you gave it a slightly different name?");
    prompts.push("Is there a memory, place, or person that makes this feeling more bearable or more sharp?");
  }

  // Dedupe + limit
  return Array.from(new Set(prompts)).slice(0, 3);
}

export function computeSimpleStats(state: DigState) {
  const byLayer = LAYERS.map(l => ({
    layer: l.key,
    count: state.artifacts.filter(a => a.layer === l.key).length,
  }));
  const totalArtifacts = state.artifacts.length;
  const revisionCount = state.revisions.length;
  const avgLoad = state.revisions.length
    ? Math.round(state.revisions.reduce((sum, r) => sum + r.presence.workingMemoryLoad, 0) / state.revisions.length)
    : state.currentPresence.workingMemoryLoad;

  return { byLayer, totalArtifacts, revisionCount, avgLoad };
}
